<script setup>
import Avatar from 'next/avatar/Avatar.vue';
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFunctionGetter, useMapGetter } from 'dashboard/composables/store';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';

import groupsService from '/plugins/quepasa-inbox/src/services/groups.js';
import { isQuePasaInbox } from '/plugins/quepasa-inbox/src/helpers/inboxHelpers.js';
import {
  isGroupIdentifier,
  normalizeGroupParticipant,
} from '/plugins/quepasa-inbox/src/helpers/groupHelpers.js';

const props = defineProps({
  searchKey: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['selectMention']);

const { t } = useI18n();
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentChat = useMapGetter('getSelectedChat');
const inbox = useFunctionGetter(
  'inboxes/getInbox',
  computed(() => currentChat.value?.inbox_id)
);
const currentContact = useFunctionGetter(
  'contacts/getContact',
  computed(() => currentChat.value?.meta?.sender?.id)
);

const loading = ref(false);
const participants = ref([]);
const dropdownRef = ref(null);
const selectedIndex = ref(0);

const groupIdentifier = computed(() => {
  return (
    currentContact.value?.identifier ||
    currentChat.value?.meta?.sender?.identifier ||
    ''
  );
});

const isVisible = computed(() => {
  return (
    isQuePasaInbox(inbox.value) && isGroupIdentifier(groupIdentifier.value)
  );
});

const filteredParticipants = computed(() => {
  const search = props.searchKey.trim().toLowerCase();

  return participants.value
    .map(participant => normalizeGroupParticipant(participant))
    .filter(participant => participant.mentionable)
    .filter(participant => {
      if (!search) return true;

      return (
        participant.displayName.toLowerCase().includes(search) ||
        participant.phone.includes(search)
      );
    });
});

const adjustScroll = () => {
  nextTick(() => {
    const selectedElement = dropdownRef.value?.querySelector(
      `#quepasa-group-mention-${selectedIndex.value}`
    );

    selectedElement?.scrollIntoView({
      block: 'nearest',
      behavior: 'auto',
    });
  });
};

const onSelect = () => {
  const participant = filteredParticipants.value[selectedIndex.value];
  if (!participant) return;

  emit('selectMention', `${participant.mentionToken} `);
};

useKeyboardNavigableList({
  items: filteredParticipants,
  onSelect,
  adjustScroll,
  selectedIndex,
});

const loadParticipants = async () => {
  if (
    !isVisible.value ||
    !currentAccountId.value ||
    !currentChat.value?.inbox_id
  ) {
    participants.value = [];
    return;
  }

  loading.value = true;

  try {
    const response = await groupsService.details(
      currentAccountId.value,
      currentChat.value.inbox_id,
      groupIdentifier.value
    );
    participants.value =
      response.participants || response.group?.Participants || [];
  } catch {
    participants.value = [];
  } finally {
    loading.value = false;
  }
};

watch(filteredParticipants, participantsList => {
  if (!participantsList.length) {
    selectedIndex.value = 0;
    return;
  }

  if (selectedIndex.value > participantsList.length - 1) {
    selectedIndex.value = 0;
  }
});

watch(
  groupIdentifier,
  () => {
    loadParticipants();
  },
  { immediate: true }
);
</script>

<template>
  <div>
    <template v-if="isVisible">
      <ul
        v-if="filteredParticipants.length"
        ref="dropdownRef"
        class="vertical dropdown menu mention--box bg-n-solid-1 p-1 rounded-xl text-sm overflow-auto absolute w-full z-20 shadow-md left-0 leading-[1.2] bottom-full max-h-[12.5rem] border border-solid border-n-strong"
        role="listbox"
      >
        <li
          v-for="(participant, index) in filteredParticipants"
          :id="`quepasa-group-mention-${index}`"
          :key="participant.id"
        >
          <div
            :class="{
              'bg-n-alpha-black2': index === selectedIndex,
            }"
            class="flex items-center px-2 py-1 rounded-md cursor-pointer"
            role="option"
            @click="emit('selectMention', `${participant.mentionToken} `)"
            @mouseover="selectedIndex = index"
          >
            <div class="ltr:mr-2 rtl:ml-2">
              <Avatar :name="participant.displayName" rounded-full />
            </div>
            <div
              class="overflow-hidden flex-1 max-w-full whitespace-nowrap text-ellipsis"
            >
              <h5
                class="overflow-hidden mb-0 text-sm whitespace-nowrap text-n-slate-11 text-ellipsis"
                :class="{
                  'text-n-slate-12': index === selectedIndex,
                }"
              >
                {{ participant.displayName }}
              </h5>
              <div
                class="overflow-hidden text-xs whitespace-nowrap text-ellipsis text-n-slate-10"
                :class="{
                  'text-n-slate-11': index === selectedIndex,
                }"
              >
                {{ participant.mentionToken }}
              </div>
            </div>
          </div>
        </li>
      </ul>
      <div
        v-else
        class="absolute w-full left-0 bottom-full rounded-xl border border-solid border-n-strong bg-n-solid-1 px-3 py-2 text-xs text-n-slate-11 shadow-md z-20"
      >
        {{
          loading
            ? t('QUEPASA_INBOX.GROUPS.MENTIONS.LOADING')
            : t('QUEPASA_INBOX.GROUPS.MENTIONS.EMPTY')
        }}
      </div>
    </template>
  </div>
</template>
